import { useEffect, useState, useRef } from 'react';
import { dataAdminApi } from '../api';
import { Database, RefreshCw, CheckCircle, AlertCircle, Download, Upload, ChevronDown, ChevronUp, Clock, Server, BookOpen, GraduationCap, Award } from 'lucide-react';

interface DataStatus {
  schools_total: number;
  schools_imported: number;
  majors_total: number;
  majors_imported: number;
  college_admission: { year: number; count: number }[];
  major_admission: { year: number; count: number }[];
  rank_table: { year: number; count: number }[];
  last_updated: string;
}

interface AcademicianStatus {
  last_update: string | null;
  source: string;
  schools_with_academicians: number;
  total_academicians: number;
  summary: { academy: string; count: number }[];
}

interface CrawlTask {
  id: number;
  type: string;
  status: string;
  progress: number;
  message: string;
  created_at: string;
}

export default function DataAdmin() {
  const [status, setStatus] = useState<DataStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [crawling, setCrawling] = useState(false);
  const [activeTask, setActiveTask] = useState<CrawlTask | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [expandedSection, setExpandedSection] = useState<string | null>('schools');
  const [academicianStatus, setAcademicianStatus] = useState<AcademicianStatus | null>(null);
  const [academicianCrawling, setAcademicianCrawling] = useState(false);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadStatus();
    loadAcademicianStatus();
  }, []);

  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await dataAdminApi.status();
      setStatus(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadAcademicianStatus = async () => {
    try {
      const res = await dataAdminApi.academicianStatus();
      setAcademicianStatus(res.data.data);
    } catch (e) {
      console.error('加载院士数据状态失败:', e);
    }
  };

  const startAcademicianCrawl = async () => {
    setAcademicianCrawling(true);
    setLogs(prev => [...prev, '开始更新院士数据...']);
    try {
      const res = await dataAdminApi.crawlAcademicians();
      setLogs(prev => [...prev, `院士数据更新完成！共 ${res.data.data?.total_academicians || 0} 名院士`]);
      loadAcademicianStatus();
    } catch (e: any) {
      setLogs(prev => [...prev, `院士数据更新失败: ${e.message}`]);
    } finally {
      setAcademicianCrawling(false);
    }
  };

  const startCrawl = async (type: string) => {
    setCrawling(true);
    setLogs([`开始爬取 ${type === 'schools' ? '院校数据' : type === 'majors' ? '专业数据' : '录取数据'}...`]);
    setActiveTask({ id: Date.now(), type, status: 'running', progress: 0, message: '初始化...', created_at: new Date().toISOString() });
    
    try {
      // 模拟进度更新
      const interval = setInterval(() => {
        setActiveTask(prev => {
          if (!prev) return null;
          const newProgress = Math.min(prev.progress + Math.random() * 15, 95);
          return { ...prev, progress: newProgress, message: `进度 ${Math.round(newProgress)}%` };
        });
      }, 2000);

      const res = await dataAdminApi.crawl(type);
      clearInterval(interval);
      
      setActiveTask({ 
        id: Date.now(), 
        type, 
        status: 'completed', 
        progress: 100, 
        message: '完成！', 
        created_at: new Date().toISOString() 
      });
      setLogs(prev => [...prev, '数据爬取完成！', `共获取 ${res.data.data?.count || 0} 条记录`]);
      
      setTimeout(() => {
        setCrawling(false);
        setActiveTask(null);
        loadStatus();
      }, 2000);
    } catch (e: any) {
      console.error(e);
      setLogs(prev => [...prev, `错误: ${e.message || '爬取失败'}`]);
      setActiveTask(prev => prev ? { ...prev, status: 'failed', message: '失败' } : null);
      setCrawling(false);
    }
  };

  const importBuiltin = async () => {
    setLogs([...logs, '正在导入内置院校数据...']);
    try {
      const res = await dataAdminApi.importBuiltin();
      setLogs(prev => [...prev, `成功导入 ${res.data.data?.count || 0} 所院校`]);
      loadStatus();
    } catch (e: any) {
      setLogs(prev => [...prev, `导入失败: ${e.message}`]);
    }
  };

  const getProgressColor = (percent: number) => {
    if (percent >= 90) return 'bg-green-500';
    if (percent >= 50) return 'bg-blue-500';
    if (percent >= 20) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getProgressText = (percent: number) => {
    if (percent >= 90) return '完整';
    if (percent >= 50) return '部分';
    if (percent >= 20) return '较少';
    return '缺失';
  };

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const dataSections: {
    id: string;
    title: string;
    icon: React.ReactNode;
    total?: number;
    imported?: number;
    years?: { year: number; count: number }[];
    description: string;
  }[] = [
    {
      id: 'schools',
      title: '院校基础数据',
      icon: <GraduationCap size={20} />,
      total: status?.schools_total ?? 0,
      imported: status?.schools_imported ?? 0,
      description: '985/211高校基本信息（名称、城市、类型）',
    },
    {
      id: 'college',
      title: '高校录取数据',
      icon: <Database size={20} />,
      years: status?.college_admission ?? [],
      description: '各高校在福建省历年录取分数线和位次',
    },
    {
      id: 'majors',
      title: '专业录取数据',
      icon: <BookOpen size={20} />,
      years: status?.major_admission ?? [],
      description: '各专业在各高校的历年录取数据',
    },
    {
      id: 'rank',
      title: '一分一段表',
      icon: <Server size={20} />,
      years: status?.rank_table ?? [],
      description: '福建省物理类一分一段表（2022-2024）',
    },
    {
      id: 'academician',
      title: '院士数据',
      icon: <Award size={20} />,
      total: academicianStatus?.total_academicians ?? 0,
      imported: academicianStatus?.schools_with_academicians ?? 0,
      description: `中国科学院/工程院院士与高校关联数据（${academicianStatus?.total_academicians || 0}名院士，${academicianStatus?.schools_with_academicians || 0}所高校）`,
    },
  ];

  return (
    <div className="p-3 md:p-6 max-w-5xl mx-auto">
      {/* 顶部标题 */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">数据管理中心</h1>
        <p className="text-gray-500 text-sm mt-1">
          查看数据完整度，管理数据爬取和导入
        </p>
      </div>

      {/* 数据概览卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <DataCard
          icon={<GraduationCap size={24} className="text-blue-500" />}
          label="院校数据"
          value={`${status?.schools_imported || 0}/${status?.schools_total || 0}`}
          percent={status?.schools_total ? (status.schools_imported / status.schools_total) * 100 : 0}
        />
        <DataCard
          icon={<Database size={24} className="text-green-500" />}
          label="录取数据"
          value={`${(status?.college_admission?.reduce((sum, y) => sum + y.count, 0) || 0)}条`}
          percent={85}
        />
        <DataCard
          icon={<BookOpen size={24} className="text-purple-500" />}
          label="专业数据"
          value={`${(status?.major_admission?.reduce((sum, y) => sum + y.count, 0) || 0)}条`}
          percent={70}
        />
        <DataCard
          icon={<Server size={24} className="text-amber-500" />}
          label="一分一段"
          value={`${(status?.rank_table?.reduce((sum, y) => sum + y.count, 0) || 0)}条`}
          percent={100}
        />
        <DataCard
          icon={<Award size={24} className="text-amber-600" />}
          label="院士数据"
          value={`${academicianStatus?.total_academicians || 0}人`}
          percent={academicianStatus?.total_academicians ? 100 : 0}
        />
      </div>

      {/* 数据详情 */}
      <div className="space-y-4 mb-6">
        {dataSections.map(section => (
          <div key={section.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  {section.icon}
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-800">{section.title}</h3>
                  <p className="text-sm text-gray-500">{section.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {section.years ? (
                  <div className="flex gap-2">
                    {section.years.map((y: any) => (
                      <span key={y.year} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {y.year}: {y.count}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-gray-600">
                    {section.imported}/{section.total}
                  </span>
                )}
                {expandedSection === section.id ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </div>
            </button>
            
            {expandedSection === section.id && (
              <div className="px-5 pb-5 border-t border-gray-100">
                {section.id === 'schools' && (
                  <div className="pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-600">院校入库进度</span>
                      <span className="text-sm font-medium text-gray-800">
                        {section.imported ?? 0}/{section.total ?? 0} ({Math.round(((section.imported ?? 0) / (section.total ?? 1)) * 100)}%)
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full mb-4">
                      <div 
                        className={`h-full ${getProgressColor(((section.imported ?? 0) / (section.total ?? 1)) * 100)} rounded-full transition-all`}
                        style={{ width: `${((section.imported ?? 0) / (section.total ?? 1)) * 100}%` }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startCrawl('schools')}
                        disabled={crawling}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50"
                      >
                        <RefreshCw size={14} className={crawling ? 'animate-spin' : ''} />
                        爬取院校数据
                      </button>
                      <button
                        onClick={importBuiltin}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200"
                      >
                        <Download size={14} />
                        导入内置数据
                      </button>
                    </div>
                  </div>
                )}
                
                {section.id === 'college' && (
                  <div className="pt-4">
                    <p className="text-sm text-gray-600 mb-3">高校录取数据（福建省）</p>
                    <div className="space-y-3">
                      {(section.years ?? []).map((y: any) => (
                        <div key={y.year} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{y.year}年</span>
                          <div className="flex items-center gap-3 flex-1 max-w-xs mx-4">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full">
                              <div className="h-full bg-green-500 rounded-full" style={{ width: '85%' }} />
                            </div>
                            <span className="text-sm font-medium text-gray-800">{y.count}条</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => startCrawl('college')}
                      disabled={crawling}
                      className="mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50"
                    >
                      <RefreshCw size={14} className={crawling ? 'animate-spin' : ''} />
                      爬取录取数据
                    </button>
                  </div>
                )}
                
                {section.id === 'majors' && (
                  <div className="pt-4">
                    <p className="text-sm text-gray-600 mb-3">专业录取数据</p>
                    <div className="space-y-3">
                      {(section.years ?? []).map((y: any) => (
                        <div key={y.year} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{y.year}年</span>
                          <div className="flex items-center gap-3 flex-1 max-w-xs mx-4">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full">
                              <div className="h-full bg-purple-500 rounded-full" style={{ width: '70%' }} />
                            </div>
                            <span className="text-sm font-medium text-gray-800">{y.count}条</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => startCrawl('majors')}
                      disabled={crawling}
                      className="mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50"
                    >
                      <RefreshCw size={14} className={crawling ? 'animate-spin' : ''} />
                      爬取专业数据
                    </button>
                  </div>
                )}
                
                {section.id === 'rank' && (
                  <div className="pt-4">
                    <p className="text-sm text-gray-600 mb-3">福建省一分一段表（物理类）</p>
                    <div className="space-y-3">
                      {(section.years ?? []).map((y: any) => (
                        <div key={y.year} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{y.year}年</span>
                          <div className="flex items-center gap-3 flex-1 max-w-xs mx-4">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full">
                              <div className="h-full bg-green-500 rounded-full" style={{ width: '100%' }} />
                            </div>
                            <span className="text-sm font-medium text-gray-800">{y.count}条</span>
                          </div>
                          <CheckCircle size={16} className="text-green-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {section.id === 'academician' && (
                  <div className="pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-gray-600">院士与高校关联数据</p>
                        {academicianStatus?.last_update && (
                          <p className="text-xs text-gray-400 mt-1">上次更新：{academicianStatus.last_update}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {academicianStatus?.total_academicians ? (
                          <CheckCircle size={16} className="text-green-500" />
                        ) : (
                          <AlertCircle size={16} className="text-amber-500" />
                        )}
                        <span className="text-sm font-medium text-gray-800">
                          {academicianStatus?.total_academicians || 0}名院士
                        </span>
                      </div>
                    </div>

                    {academicianStatus?.summary && academicianStatus.summary.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-500 mb-2">按学部分布</p>
                        <div className="flex flex-wrap gap-2">
                          {academicianStatus.summary.map(s => (
                            <span key={s.academy} className="text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-lg">
                              {s.academy}: {s.count}人
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={startAcademicianCrawl}
                      disabled={academicianCrawling || crawling}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-xl hover:bg-amber-700 disabled:opacity-50"
                    >
                      <RefreshCw size={14} className={academicianCrawling ? 'animate-spin' : ''} />
                      {academicianStatus?.total_academicians ? '更新院士数据' : '导入院士数据'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 爬取日志 */}
      {(logs.length > 0 || crawling) && (
        <div className="bg-gray-900 rounded-2xl p-4 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-200 flex items-center gap-2">
              <Clock size={16} />
              爬取日志
            </h3>
            {activeTask && (
              <div className="flex items-center gap-2">
                <RefreshCw size={14} className={`text-blue-400 ${crawling ? 'animate-spin' : ''}`} />
                <span className="text-sm text-gray-400">{activeTask.message}</span>
              </div>
            )}
          </div>
          <div className="h-48 overflow-y-auto font-mono text-xs text-green-400 space-y-1">
            {logs.map((log, i) => (
              <div key={i} className="opacity-80">{'>'} {log}</div>
            ))}
            {crawling && <div className="opacity-60 animate-pulse">{'>'} 等待响应...</div>}
            <div ref={logEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}

function DataCard({ icon, label, value, percent }: { 
  icon: React.ReactNode; label: string; value: string; percent: number 
}) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center gap-3 mb-3">
        {icon}
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-800 mb-2">{value}</div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all ${
            percent >= 90 ? 'bg-green-500' : percent >= 50 ? 'bg-blue-500' : 'bg-amber-500'
          }`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
}
